/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LAS CATEGORÍAS DE NEGOCIO — UNA SOLA LISTA, EN UN SOLO SITIO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ── POR QUÉ ESTE ARCHIVO EXISTE ──────────────────────────────────────────
 *
 * Había DOS listas, en dos archivos distintos: la que elegía el restaurante al
 * registrarse (`restaurant-registration.tsx`) y la que navegaba el cliente
 * (`(tabs)/food.tsx`). Siete de las ocho coincidían. La octava no:
 *
 *     el restaurante guardaba  →  «Comida Dominicana»   (y era el valor por defecto)
 *     el cliente buscaba       →  «Dominicana»
 *
 * Y el filtro del servidor es `where('category', '==', …)`, comparación
 * exacta. O sea que al tocar esa categoría, Firestore NI SIQUIERA DEVOLVÍA
 * esos restaurantes. Ni el filtro de pantalla los salvaba: también usaba `===`.
 *
 * Eso significa que TODO restaurante que no cambiara el valor por defecto
 * —que son los que menos saben de la app, los que acaban de entrar— quedaba
 * invisible en su propia categoría.
 *
 * ── LA CAUSA DE FONDO NO ERA EL NOMBRE ───────────────────────────────────
 *
 * Era que se GUARDABA EL TEXTO QUE SE VE EN PANTALLA. Con eso, basta con que
 * alguien escriba la etiqueta distinta en dos sitios —o le cambie una tilde, o
 * la traduzca— para que un negocio desaparezca sin que salte ningún error.
 * Y ya había pasado una vez: `food-category.tsx` traía un apaño que quitaba el
 * prefijo «comida », con un comentario explicando el choque. El apaño arreglaba
 * UNA de las dos pantallas.
 *
 * Aquí se separa en dos cosas que no se tocan entre sí:
 *
 *     `id`      lo que se GUARDA en Firestore. No se traduce, no se renombra,
 *               no lleva tildes ni mayúsculas. Es el contrato con los datos.
 *     `nombre`  lo que LEE la persona. Se puede cambiar cuando se quiera.
 *
 * El día que se traduzca la app al inglés, o que se le cambie el nombre a una
 * categoría, ningún restaurante se cae.
 *
 * ── «COMIDA LOCAL», Y NO EL GENTILICIO ───────────────────────────────────
 *
 * Decisión del fundador (4-oct-2026): «la app no puede mencionar nada que
 * tenga identificador como marca país» en lo que el usuario ve. Hoy el mercado
 * es República Dominicana y eso no cambia —la moneda, la cédula y el formato de
 * placa se quedan como están, porque son cómo funciona el negocio hoy—, pero
 * una CATEGORÍA con gentilicio es marca país y además no viaja: el día que haya
 * un restaurante en otro sitio, «Comida local» sigue significando lo correcto
 * para quien lo lee allí.
 *
 * ── SI SE AÑADE UNA CATEGORÍA ────────────────────────────────────────────
 *
 * Se añade AQUÍ y aparece sola en las dos pantallas. No hay segunda lista que
 * actualizar — ese era justamente el problema.
 *
 * ⚠️ Y NUNCA se cambia un `id` que ya esté en producción: los restaurantes
 * guardados con el id viejo quedarían huérfanos. Para renombrar, se cambia el
 * `nombre`, que para eso está separado.
 */

export type CategoriaDeNegocio = {
  /** Lo que se guarda en Firestore. Estable para siempre. */
  id: string;
  /** Lo que lee la persona. Se puede cambiar sin romper nada. */
  nombre: string;
  icono: string;
};

export const CATEGORIAS_DE_NEGOCIO: CategoriaDeNegocio[] = [
  { id: 'local', nombre: 'Comida local', icono: '🥘' },
  { id: 'pizza', nombre: 'Pizza', icono: '🍕' },
  { id: 'hamburguesas', nombre: 'Hamburguesas', icono: '🍔' },
  { id: 'pollo', nombre: 'Pollo', icono: '🍗' },
  { id: 'sushi', nombre: 'Sushi', icono: '🍣' },
  { id: 'cafe', nombre: 'Café', icono: '☕' },
  { id: 'supermercado', nombre: 'Supermercado', icono: '🛒' },
  { id: 'farmacia', nombre: 'Farmacia', icono: '💊' },
  // ── 4-oct-2026: LOS NEGOCIOS QUE NO TENÍAN DÓNDE PONERSE ────────────────
  //
  // Había OCHO categorías: seis de comida, supermercado y farmacia. O sea que
  // un minimarket, una ferretería o una licorería que quisiera registrarse no
  // se veía en ninguna, y la última casilla de un formulario no es sitio para
  // que alguien decida que esta app no es para él.
  //
  // El fundador lo pidió así: «en ese apartado debe decir que en ninguna otra
  // parte está supermercado, farmacia entre otros negocios a registrar». Para
  // poder decirle eso a alguien, primero tiene que caber.
  //
  // `otro` es la más importante de las siete y la que nunca hay que quitar: es
  // la que impide que un negocio legítimo se vaya por no encontrarse en una
  // lista. Lo que se guarda es el `id`, así que mañana se puede sacar un tipo
  // nuevo de ahí sin romper a nadie — basta con que el dueño lo cambie.
  { id: 'minimarket', nombre: 'Minimarket / colmado', icono: '🏪' },
  { id: 'bebidas', nombre: 'Bebidas y licores', icono: '🍺' },
  { id: 'reposteria', nombre: 'Repostería y panadería', icono: '🎂' },
  { id: 'ferreteria', nombre: 'Ferretería', icono: '🔧' },
  { id: 'belleza', nombre: 'Belleza y cuidado personal', icono: '💅' },
  { id: 'mascotas', nombre: 'Mascotas', icono: '🐾' },
  { id: 'otro', nombre: 'Otro negocio', icono: '📦' },
];

/**
 * LO QUE SE GUARDABA ANTES → EL ID DE AHORA.
 *
 * Existe por una razón concreta: los restaurantes que ya están registrados
 * tienen en `category` el TEXTO viejo. La migración del servidor
 * (`functions/src/migracionCategorias.ts`) los pasa a su id, pero esta tabla se
 * queda aquí igualmente como red de seguridad, para que la app sepa leer un
 * documento que todavía no se haya migrado en vez de esconderlo.
 *
 * No se borra aunque la migración ya haya corrido: un documento viejo puede
 * aparecer desde una copia de seguridad, o desde un teléfono que guardó algo
 * sin conexión y lo sube días después.
 */
const EQUIVALENCIAS_VIEJAS: Record<string, string> = {
  'comida dominicana': 'local',
  dominicana: 'local',
  pizza: 'pizza',
  hamburguesas: 'hamburguesas',
  pollo: 'pollo',
  sushi: 'sushi',
  cafe: 'cafe',
  café: 'cafe',
  supermercado: 'supermercado',
  farmacia: 'farmacia',
};

/**
 * Devuelve el id de categoría de lo que venga guardado: ya sea un id nuevo o
 * el texto viejo. Si no reconoce nada, devuelve null — y quien llama decide
 * qué hacer, en vez de inventarse una categoría.
 */
export function idDeCategoria(guardado: unknown): string | null {
  const bruto = String(guardado ?? '').trim();
  if (!bruto) return null;
  const minuscula = bruto.toLowerCase();
  if (CATEGORIAS_DE_NEGOCIO.some((c) => c.id === minuscula)) return minuscula;
  return EQUIVALENCIAS_VIEJAS[minuscula] ?? null;
}

/** El nombre que se le enseña a la persona. Para pintar, nunca para comparar. */
export function nombreDeCategoria(guardado: unknown): string | null {
  const id = idDeCategoria(guardado);
  if (!id) return null;
  return CATEGORIAS_DE_NEGOCIO.find((c) => c.id === id)?.nombre ?? null;
}
